import { useEffect, useState } from 'react';
import { Button, Table, TextInput, Group, Badge, Alert, Paper, Loader, Text, Modal, SimpleGrid, Stack, Divider, Tabs } from '@mantine/core';
import { IconEdit, IconShoppingCart, IconFileInvoice, IconUserPlus, IconSearch, IconAlertCircle } from '@tabler/icons-react';
import { formatPrice } from '../utils/formatPrice';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from '../context/TranslationContext';
import api, { API_BASE_URL } from '../services/api';
import AddEditUserModal from './AddEditUserModal';
import AdminCreateOrderModal from './AdminCreateOrderModal';
import OngoingOrderModal from './OngoingOrderModal';
import CreateUserModal from './CreateUserModal';
import { useLanguages } from './LanguageTabs';

function groupTabKey(billingAddressId) {
  return billingAddressId || '__no_billing_address__';
}

export default function UserManagement() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const languages = useLanguages();
  const [usersList, setUsersList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingUser, setEditingUser] = useState(null);
  const [orderingUser, setOrderingUser] = useState(null);
  const [ongoingOrderId, setOngoingOrderId] = useState(null);
  const [creatingUser, setCreatingUser] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusMessage, setStatusMessage] = useState(null);
  const [statusType, setStatusType] = useState('');

  // Monthly invoice modal state
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [previewUser, setPreviewUser] = useState(null);
  const [previewMonth, setPreviewMonth] = useState('');
  const [invoiceGroups, setInvoiceGroups] = useState([]);
  const [activeGroupId, setActiveGroupId] = useState(null);
  const [invoicePreview, setInvoicePreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [issuing, setIssuing] = useState(false);

  const formatYearMonth = (date) =>
    `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const data = await api.getUsers();
      setUsersList(data);
    } catch (err) {
      console.error(err);
      showStatus(t?.failedFetchUsers || 'Failed to fetch users', 'error');
    } finally {
      setLoading(false);
    }
  };

  const showStatus = (message, type) => {
    setStatusMessage(message);
    setStatusType(type);
    setTimeout(() => {
      setStatusMessage(null);
      setStatusType('');
    }, 5000);
  };

  const handleEdit = (u) => {
    setEditingUser(u);
  };

  const handleCloseEditModal = () => {
    setEditingUser(null);
  };

  const handleCreateUser = () => {
    setCreatingUser(true);
  };

  const handleCloseCreateModal = () => {
    setCreatingUser(false);
  };

  const handleCreateUserSuccess = () => {
    handleCloseCreateModal();
    fetchUsers();
    showStatus(t('createUserSuccess', 'User created successfully.'), 'success');
  };

  const handleSave = async (userId, updatedData) => {
    try {
      await api.updateUser(userId, updatedData);
      fetchUsers();
      handleCloseEditModal();
    } catch (error) {
      console.error('Saving failed', error);
      showStatus(t?.failedSaveUser || 'Failed to save user updates', 'error');
    }
  };

  const handleCreateOrder = (u) => {
    setOrderingUser(u);
  };

  const handleCloseOrderModal = () => {
    setOrderingUser(null);
  };

  const handleOrderSuccess = () => {
    handleCloseOrderModal();
    showStatus(t?.orderCreatedSuccessfully || 'Order created successfully on behalf of user.', 'success');
  };

  const closePreviewModal = () => {
    setPreviewModalOpen(false);
    setInvoicePreview(null);
    setInvoiceGroups([]);
    setPreviewUser(null);
    setActiveGroupId(null);
  };

  const loadPreviewForGroup = async (u, billingAddressId, month) => {
    try {
      setPreviewLoading(true);
      setInvoicePreview(null);
      const data = await api.previewMonthlyInvoice(u.id, t.langCode, month, billingAddressId);
      setInvoicePreview(data);
    } catch (err) {
      console.error(err);
      setInvoicePreview(null);
      showStatus(err.message || t('issueMonthlyInvoiceError', 'Failed to preview invoice.'), 'error');
    } finally {
      setPreviewLoading(false);
    }
  };

  const handlePreviewInvoice = async (u) => {
    setPreviewUser(u);
    setInvoiceGroups([]);
    setInvoicePreview(null);
    setActiveGroupId(null);
    setPreviewModalOpen(true);

    const month = formatYearMonth(new Date());
    setPreviewMonth(month);

    try {
      const data = await api.getMonthlyInvoiceGroups(u.id, month);
      setInvoiceGroups(data.groups);
      if (data.groups.length > 0) {
        const first = data.groups[0];
        setActiveGroupId(first.billingAddressId);
        await loadPreviewForGroup(u, first.billingAddressId, month);
      }
    } catch (err) {
      console.error(err);
      showStatus(err.message || t('issueMonthlyInvoiceError', 'Failed to preview invoice.'), 'error');
      setPreviewModalOpen(false);
    }
  };

  const handleGroupChange = (tabKey) => {
    if (tabKey === groupTabKey(activeGroupId)) return;
    const matchingGroup = invoiceGroups.find(g => groupTabKey(g.billingAddressId) === tabKey);
    if (!matchingGroup) return;
    setActiveGroupId(matchingGroup.billingAddressId);
    loadPreviewForGroup(previewUser, matchingGroup.billingAddressId, previewMonth);
  };

  const handleConfirmIssueInvoice = async () => {
    if (!previewUser || !invoicePreview) return;
    try {
      setIssuing(true);
      const data = await api.issueMonthlyInvoice(previewUser.id, invoicePreview.previewKey);
      showStatus(data.message || t('issueMonthlyInvoiceSuccess', 'Monthly invoice issued successfully.'), 'success');
      // Refresh groups to remove the just-issued group
      const refreshed = await api.getMonthlyInvoiceGroups(previewUser.id, previewMonth);
      setInvoiceGroups(refreshed.groups);
      if (refreshed.groups.length > 0) {
        const first = refreshed.groups[0];
        setActiveGroupId(first.billingAddressId);
        await loadPreviewForGroup(previewUser, first.billingAddressId, previewMonth);
      } else {
        setInvoicePreview(null);
        setActiveGroupId(null);
      }
    } catch (err) {
      console.error(err);
      showStatus(err.message || t('issueMonthlyInvoiceError', 'Failed to issue invoice.'), 'error');
    } finally {
      setIssuing(false);
    }
  };

  const filteredUsers = usersList.filter(u => 
    u.name?.toLowerCase().includes(searchQuery.toLowerCase()) || 
    u.email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (!user?.is_admin) return <div>Access Denied</div>;

  return (
    <div className="dashboard-view-wrapper">
      <header className="dashboard-page-header">
        <div>
          <h1 className="dashboard-page-title">Customer Management</h1>
          <p className="dashboard-page-description">Manage user accounts and view details.</p>
        </div>
      </header>

      {statusMessage && (
        <Alert
          icon={<IconAlertCircle size={16} />}
          color={statusType === 'error' ? 'red' : 'green'}
          mb="md"
          role={statusType === 'error' ? 'alert' : 'status'}
          aria-live={statusType === 'error' ? 'assertive' : 'polite'}
        >
          {statusMessage}
        </Alert>
      )}

      <Group justify="space-between" mb="md">
        <TextInput
          placeholder="Search users by name or email…"
          leftSection={<IconSearch size={16} aria-hidden="true" />}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.currentTarget.value)}
          style={{ flex: 1, minWidth: 240 }}
          aria-label="Search users by name or email"
        />
        <Button
          leftSection={<IconUserPlus size={16} aria-hidden="true" />}
          onClick={handleCreateUser}
        >
          {t('createUser', 'Create User')}
        </Button>
      </Group>

      {loading ? (
        <Loader role="status" aria-live="polite" />
      ) : (
        <Paper withBorder>
          <Table.ScrollContainer minWidth={900}>
            <Table verticalSpacing="md" horizontalSpacing="md" highlightOnHover>
              <caption className="sr-only">Customers list with name, email, account type, admin/invoicing flags, account balance, creation date, and admin actions</caption>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th scope="col">Name</Table.Th>
                  <Table.Th scope="col">Email</Table.Th>
                  <Table.Th scope="col">Type</Table.Th>
                  <Table.Th scope="col">Admin?</Table.Th>
                  <Table.Th scope="col">Monthly Invoicing?</Table.Th>
                  <Table.Th scope="col">{t('accountBalance', 'Account Balance')}</Table.Th>
                  <Table.Th scope="col">{t('createdAt', 'Created')}</Table.Th>
                  <Table.Th scope="col">Actions</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {filteredUsers.map(u => (
                  <Table.Tr key={u.id}>
                    <Table.Th scope="row">
                      <Text fw={500}>{u.name}</Text>
                    </Table.Th>
                    <Table.Td>{u.email}</Table.Td>
                    <Table.Td>
                      <Badge color={u.user_type === 'business' ? 'blue' : 'gray'} variant="light">{u.user_type}</Badge>
                    </Table.Td>
                    <Table.Td>{u.is_admin ? 'Yes' : 'No'}</Table.Td>
                    <Table.Td>{u.monthly_invoicing_enabled ? 'Yes' : 'No'}</Table.Td>
                    <Table.Td>
                      {u.account_balance != null ? (
                        <Text c={Number(u.account_balance) > 0 ? 'green' : Number(u.account_balance) < 0 ? 'red' : undefined} fw={600}>
                          {formatPrice(u.account_balance)}
                        </Text>
                      ) : '—'}
                    </Table.Td>
                    <Table.Td>{u.created_at ? new Date(u.created_at).toLocaleDateString(t.langCode === 'de' ? 'de-CH' : 'en-CH', { year: 'numeric', month: 'short', day: 'numeric' }) : '—'}</Table.Td>
                    <Table.Td>
                      <Group gap="xs">
                        <Button variant="subtle" size="xs" leftSection={<IconEdit size={14} aria-hidden="true" />} aria-label={`Edit user ${u.name}`} onClick={() => handleEdit(u)}>
                          Edit
                        </Button>
                        <Button size="xs" leftSection={<IconShoppingCart size={14} aria-hidden="true" />} aria-label={`Create order for ${u.name}`} onClick={() => handleCreateOrder(u)}>
                          Create Order
                        </Button>

                        {u.monthly_invoicing_enabled ? (
                          <Button variant="default" size="xs" leftSection={<IconFileInvoice size={14} aria-hidden="true" />} aria-label={`Preview monthly invoice for ${u.name}`} onClick={() => handlePreviewInvoice(u)}>
                            {t('previewInvoiceButton', 'Preview Invoice')}
                          </Button>
                        ) : null}
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                ))}
                {filteredUsers.length === 0 && (
                  <Table.Tr>
                    <Table.Td colSpan={8}>
                      <Text c="dimmed">No users found matching "{searchQuery}"</Text>
                    </Table.Td>
                  </Table.Tr>
                )}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        </Paper>
      )}

      {editingUser && (
        <AddEditUserModal 
          user={editingUser} 
          onClose={handleCloseEditModal} 
          onSave={handleSave} 
        />
      )}

      {creatingUser && (
        <CreateUserModal
          onClose={handleCloseCreateModal}
          onSuccess={handleCreateUserSuccess}
          languages={languages}
        />
      )}


      {orderingUser && (
        <AdminCreateOrderModal 
          user={orderingUser} 
          onClose={handleCloseOrderModal} 
          onSuccess={handleOrderSuccess}
          onShowOngoing={(id) => { setOngoingOrderId(id); handleCloseOrderModal(); }}
        />
      )}

      {ongoingOrderId && (
        <OngoingOrderModal
          orderId={ongoingOrderId}
          onClose={() => setOngoingOrderId(null)}
          onChanged={() => {}}
        />
      )}


      <Modal
        opened={previewModalOpen}
        onClose={closePreviewModal}
        title={t('previewMonthlyInvoiceModalTitle', 'Monthly Invoice Preview')}
        centered
        size="xl"
        trapFocus={false}
        aria-label={t('previewMonthlyInvoiceModalTitle', 'Monthly Invoice Preview')}
      >
        {invoiceGroups.length === 0 && !previewLoading && (
          <Text c="dimmed">{t('issueMonthlyInvoiceNone', 'No unpaid monthly orders found for this month to invoice.')}</Text>
        )}
        {previewLoading && invoiceGroups.length === 0 && (
          <Loader role="status" aria-live="polite" />
        )}
        {invoiceGroups.length > 0 && (
          <Tabs value={groupTabKey(activeGroupId)} onChange={handleGroupChange} aria-label="Monthly invoice billing address groups">
            <Tabs.List grow>
              {invoiceGroups.map(g => (
                <Tabs.Tab key={groupTabKey(g.billingAddressId)} value={groupTabKey(g.billingAddressId)}>
                  {g.billingAddressId ? (g.label || t('previewInvoiceGroupUnknown', 'Unknown address')) : t('previewInvoiceGroupNone', 'No billing address')}
                </Tabs.Tab>
              ))}
            </Tabs.List>
            {invoiceGroups.map(g => (
              <Tabs.Panel key={groupTabKey(g.billingAddressId)} value={groupTabKey(g.billingAddressId)} pt="md">
                {previewLoading && activeGroupId === g.billingAddressId ? (
                  <Loader role="status" aria-live="polite" />
                ) : invoicePreview && activeGroupId === g.billingAddressId ? (
                  <Stack>
                    <SimpleGrid cols={{ base: 2, sm: 4 }} spacing="sm">
                      <div>
                        <Text size="xs" c="dimmed">{t('previewOrderCount', 'Orders')}</Text>
                        <Text fw={600}>{invoicePreview.summary.orderCount}</Text>
                      </div>
                      <div>
                        <Text size="xs" c="dimmed">{t('previewSubtotal', 'Subtotal')}</Text>
                        <Text fw={600}>{formatPrice(invoicePreview.summary.subtotal)}</Text>
                      </div>
                      <div>
                        <Text size="xs" c="dimmed">{t('previewVat', 'VAT')}</Text>
                        <Text fw={600}>{formatPrice(invoicePreview.summary.vatAmount)}</Text>
                      </div>
                      <div>
                        <Text size="xs" c="dimmed">{t('previewTotal', 'Total')}</Text>
                        <Text fw={600} c="blue">{formatPrice(invoicePreview.summary.total)}</Text>
                      </div>
                    </SimpleGrid>
                    {Number(invoicePreview.summary.shippingCost) > 0 && (
                      <Text size="sm" c="dimmed">
                        {t('previewShipping', 'Shipping')}: {formatPrice(invoicePreview.summary.shippingCost)}
                      </Text>
                    )}
                    <Divider />
                    <Text size="sm" c="dimmed">
                      {t('previewInvoiceId', 'Invoice ID')}: {invoicePreview.invoiceId}
                    </Text>
                    <iframe
                      src={`${API_BASE_URL}/admin/preview-pdf/${invoicePreview.previewKey}`}
                      title={t('previewPdfAriaLabel', 'Invoice PDF preview')}
                      width="100%"
                      height="500px"
                      style={{ border: 'none' }}
                    />
                    <Group justify="flex-end" mt="md">
                      <Button variant="default" onClick={closePreviewModal}>
                        {t('previewClose', 'Close')}
                      </Button>
                      <Button
                        color="blue"
                        loading={issuing}
                        onClick={handleConfirmIssueInvoice}
                      >
                        {t('previewIssueButton', 'Issue Invoice')}
                      </Button>
                    </Group>
                  </Stack>
                ) : null}
              </Tabs.Panel>
            ))}
          </Tabs>
        )}
      </Modal>
    </div>
  );
}